import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { initializeI18n } from "@/i18n/i18n";
import { postLoginUser } from "@/services/postLoginUser";
import { loadMaps } from "@/shared/utils/maps";
import { fetchCommonConfig } from "@/store/adminSlice";

import { normalizeAccount } from "../auth/utils";
import { useAuth } from "../hooks/useAuth";
import { selectAuthStatus, setAccount, setStatus } from "../store/authSlice";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { fetchUserDetailsByEmail, getFluxxId, resetRoleState } from "../store/userSlice";
import { setEncryptionKey } from "./bootstrapMsal";

export default function AuthInitializer() {
  const isAuthenticated = useIsAuthenticated();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const authStatus = useAppSelector(selectAuthStatus);
  const { postLogin } = useAuth();
  const { instance } = useMsal();

  // const [userId, setUserId] = React.useState<string | null>(null);

  //Prevent duplicate runs
  const hasInitializedRef = React.useRef(false);
  const hasInitializedI18nRef = React.useRef(false);

  React.useEffect(() => {
    const run = async () => {
      //Handle logout case
     if (!isAuthenticated) {
  dispatch(resetRoleState());
  dispatch(setStatus("signedOut"));
  dispatch(setAccount(null));

  hasInitializedRef.current = false;
  hasInitializedI18nRef.current = false;

  return;
}

      //Prevent duplicate execution
      if (hasInitializedRef.current) return;

      hasInitializedRef.current = true; //lock immediately

      try {
        dispatch(setStatus("pending"));

        //Get active account safely
        let acc = instance.getActiveAccount();

        if (!acc) {
          const accounts = instance.getAllAccounts();
          if (accounts.length === 0) {
            console.warn("No MSAL accounts found");
            return;
          }

          acc = accounts[0];
          instance.setActiveAccount(acc);
        }

        const normalizedAcc = normalizeAccount(acc);
        dispatch(setAccount(normalizedAcc));

       

        // console.log("[AuthInitializer] Starting post-login bootstrap");

        // ✅ SHOULD succeed
        await postLogin();

       try {
  const commonConfig =
    await dispatch(fetchCommonConfig()).unwrap();

  if (commonConfig?.encryptionKey) {
    setEncryptionKey(commonConfig.encryptionKey);
  }
} catch (err) {
  console.warn("Failed to load common config", err);
}

         if (!hasInitializedI18nRef.current) {
          await initializeI18n();
          hasInitializedI18nRef.current = true;
        }

        const username = normalizedAcc.username;

        const results = await Promise.allSettled([
          postLoginUser(username),
          dispatch(getFluxxId(username)).unwrap(),
          dispatch(fetchUserDetailsByEmail(username)).unwrap(),
          loadMaps(),
        ]);

        const apiNames = [
          "postLoginUser",
          "getFluxxId",
          "fetchUserDetailsByEmail",
          "loadMaps",
        ];

        results.forEach((result, index) => {
          if (result.status === "rejected") {
            console.warn(`[AuthInitializer] ${apiNames[index]} failed:`, result.reason);
          }
        });

        //Mark success
        dispatch(setStatus("signedIn"));

        //Navigate once
        if (location.pathname === "/login" || location.pathname === "/") {
          navigate("/dashboard", { replace: true });
        }
      } catch (err) {
        console.error("[AuthInitializer] Critical failure:", err);
        dispatch(setStatus("signedIn"));
      }
    };

    void run();
  }, [isAuthenticated, postLogin, instance, dispatch, navigate, location, authStatus]);

  return null;
}
